import { Component, Inject, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import { IPostMessageEventTarget, PostMessageBridgeFactory } from "@tekool/ngx-post-message-angular-9";
import { FormDeterminant } from "../../models/form-determinant";
import { LogicGroup } from "../../models/jsonlogic/logic-group";

import { FormDeterminantControllerService } from "../../services/form-controller/form-determinant-controller.service";
import { LanguageControllerService } from "../../services/language-controller/language-controller.service";
import {Store} from "../../services/store.service";
import * as _ from "lodash";
import {DeterminantControllerService} from "../../services/registration-controller/determinant-controller.service";

/**
 * This determinant window is used in formiojs using iframe.
 */
@Component({
  selector: "formio-determinant-builder",
  templateUrl: "./formio-determinant-builder.component.html",
  providers: [FormDeterminantControllerService],
})
export class FormioDeterminantBuilderComponent implements OnInit {

  @Input() public serviceId: string;
  @Input() public formDeterminantId: string;
  @Input() public componentKey: string;
  @Input() public direct: boolean = false;
  @Input() public determinantMapById: { [key: string]: string } = {};
  @Output() public changeDeterminants: EventEmitter<any> = new EventEmitter();
  public formDeterminant: FormDeterminant = new FormDeterminant();
  public readonly: boolean = false;
  public jsonLogic: any;

  constructor(private route: ActivatedRoute, public store: Store, public translate: TranslateService,
              private formDeterminantControllerService: FormDeterminantControllerService,
              private determinantService: DeterminantControllerService,
              private languageService: LanguageControllerService,
              @Inject(PostMessageBridgeFactory) private bridgeFactory: PostMessageBridgeFactory) {

        const parentWindow: IPostMessageEventTarget = window.top;
        const iFrame: IPostMessageEventTarget = window;
        translate.setDefaultLang(store.state.common.lang);
        languageService.getLanguages().subscribe(
          (data) => {
            translate.addLangs(data.map((l) => l.code));
          });

        bridgeFactory.makeInstance()
            .setEnableLogging(false)
            .connect(iFrame, parentWindow)
            .makeBridge("FormBuilder")
            .makeBridge("UpdateDeterminant");
  }

  public ngOnInit() {
    if (!this.serviceId && !this.componentKey && !this.formDeterminantId) {
      this.route.params.subscribe((params) => {
        this.serviceId = params.serviceId;
        this.store.state.common.serviceId = params.serviceId;
      });
      this.route.queryParamMap.subscribe((queryParams) => {
        this.formDeterminantId = queryParams.get("formDeterminantId");
        this.componentKey = queryParams.get("key");
        this.readonly = queryParams.get("readonly") && queryParams.get("readonly").toUpperCase() === "TRUE";
        if (!Object.keys(this.determinantMapById).length) {
          this.getServiceDeterminantKeyName(() => {
            return this.loadFieldDeterminant();
          });
        }
      });
    } else {
      // direct
      this.loadFieldDeterminant();
    }
  }

  public getServiceDeterminantKeyName(cb) {
    this.determinantService.getServiceDeterminantIdAndName(this.serviceId).subscribe(
      (data) => {
        Object.assign(this.determinantMapById, data);
        if (cb) { cb(); }
      },
    );
  }

  public closeEdit() {
    this.loadFieldDeterminant();
    this.store.state.common.determinantSection.topLayer = "list";
    this.store.state.common.determinantSection.determinant = {};
  }

  private loadFieldDeterminant() {
    const cb = (data: FormDeterminant) => {
      const formDeterminant = (data ? data : this.formDeterminant);
      if (!formDeterminant.jsonDeterminant) {
        this.formDeterminant.jsonDeterminant = [new LogicGroup()];
      }
      if (!_.isEqual(this.formDeterminant, formDeterminant)) {
        this.formDeterminant = formDeterminant;
        this.sendMessage();
      }

    };
    if (this.formDeterminantId) {
      this.formDeterminantControllerService.getById(this.formDeterminantId).subscribe(cb);
    } else {
      this.formDeterminantControllerService.getFormDeterminant(this.serviceId, this.componentKey).subscribe(cb);
    }
  }

  public saveDeterminantValue(determinantValue: boolean) {
    this.formDeterminant.value = determinantValue;
    this.saveDeterminant(this.formDeterminant.jsonDeterminant);
  }

  public saveDeterminant(json: any) {
    this.sendSaveProcessingEvent();
    this.formDeterminant.jsonDeterminant = json;
    this.formDeterminant.key = null;
    const cb = (data: FormDeterminant) => {
      this.formDeterminant = (data ? data : this.formDeterminant);
      this.sendMessage();
      this.sendSaveProcessingEvent(false);
    };
    if (this.formDeterminant.id) {
      this.formDeterminantControllerService.saveFormDeterminant(this.formDeterminant).subscribe(cb);
    } else {
      this.formDeterminantControllerService.createFormDeterminant(this.serviceId, this.formDeterminant).subscribe(cb);
    }
      // what does this dO?
    this.changeDeterminants.emit(this.formDeterminant.jsonDeterminant);
  }

    private sendSaveProcessingEvent(state = true) {
        window.top.postMessage([{
            channel: "FormBuilder",
            message: JSON.stringify({event: "saveProcessing", state}),
        }], "*");
    }

  public static findAllPossibleDeterminantIds(json) {
    const ids = new Set();

    const traverse = (obj) => {
      if (obj !== null && typeof obj === "object") {
        Object.entries(obj).forEach(([key, value]) => {
          if (key === "determinantId" || key === "id") {
            ids.add(value);
          } else if (typeof value === "object") {
            traverse(value);
          }
        });
      }
    };

    traverse(json);
    return ids;
  }

    private sendMessage(): void {
        let determinantIds = [];
        if (this.formDeterminant.jsonDeterminant) {
            let conditions;
            if (this.formDeterminant.jsonDeterminant instanceof Array) {
                conditions = this.formDeterminant.jsonDeterminant;
            } else {
                conditions = JSON.parse(this.formDeterminant.jsonDeterminant);
            }
            if (Object.keys(this.determinantMapById).length) {
              determinantIds = Array.from(FormioDeterminantBuilderComponent.findAllPossibleDeterminantIds(conditions));
            } else {
              for (const group of conditions) {
                for (const item of group.items) {
                  // support old style of jsonDeterminants obj structure
                  determinantIds.push(item.determinantId || item.id ||
                    (Array.isArray(item.items) && item.items.length > 0 &&
                      (item.items[0].determinantId || item.items[1]?.determinantId || item.items[0].id || item.items[1]?.id)) || null);
                }
              }
            }
        }
        determinantIds = determinantIds.filter((id) => {
          return id != null && (!Object.keys(this.determinantMapById).length || this.determinantMapById.hasOwnProperty(id));
        }); // get rid of possible nulls
        const message = {
        key: this.componentKey,
        count: determinantIds.length,
        changes: { formDeterminantId: this.formDeterminant.id, determinantIds},
      };
        const messageString: string = JSON.stringify(message);
        window.top.postMessage([{channel: "FormBuilder", message: messageString}], "*");
  }

}
